// Custom marker icons for the MapView component
import L from 'leaflet';

/**
 * Creates a custom location marker icon with a number
 * @param {number} index - The index of the location (1-based)
 * @param {string} color - The color of the marker (optional, defaults to brand blue)
 * @returns {L.Icon} Leaflet Icon instance
 */
const createLocationMarkerIcon = (index = 0, color = '#276ACD') => {
  // Create SVG for numbered marker
  const markerColor = color; // Use provided color or default brand blue
  const markerSvg = `
    <svg width="30" height="40" viewBox="0 0 30 40" xmlns="http://www.w3.org/2000/svg">
      <path d="M15 0C6.716 0 0 6.716 0 15C0 23.284 15 40 15 40C15 40 30 23.284 30 15C30 6.716 23.284 0 15 0Z" fill="${markerColor}"/>
      <circle cx="15" cy="15" r="11" fill="white"/>
      <text x="15" y="20" font-family="Arial" font-size="14" font-weight="bold" text-anchor="middle" fill="${markerColor}">${index + 1}</text>
    </svg>
  `;

  const svgUri = `data:image/svg+xml;base64,${btoa(markerSvg.trim())}`;
  
  return new L.Icon({
    iconUrl: svgUri,                      // SVG data URI
    iconSize: [30, 40],                   // Size of the marker
    iconAnchor: [15, 40],                 // Bottom center of the marker
    popupAnchor: [0, -35],                // Position of the popup
    className: 'walkabout-numbered-marker' // CSS class for the marker
  });
};

/**
 * Creates a custom parking marker icon with a "P"
 * @returns {L.Icon} Leaflet Icon instance
 */
const createParkingMarkerIcon = () => {
  // Create SVG for parking marker with green color to match the card badges
  const markerColor = '#38A169'; // Green color matching Chakra UI green.500
  const markerSvg = `
    <svg width="30" height="40" viewBox="0 0 30 40" xmlns="http://www.w3.org/2000/svg">
      <path d="M15 0C6.716 0 0 6.716 0 15C0 23.284 15 40 15 40C15 40 30 23.284 30 15C30 6.716 23.284 0 15 0Z" fill="${markerColor}"/>
      <circle cx="15" cy="15" r="11" fill="white"/>
      <text x="15" y="20" font-family="Arial" font-size="14" font-weight="bold" text-anchor="middle" fill="${markerColor}">P</text>
    </svg>
  `;

  const svgUri = `data:image/svg+xml;base64,${btoa(markerSvg.trim())}`;
  
  return new L.Icon({
    iconUrl: svgUri,                      // SVG data URI
    iconSize: [30, 40],                   // Size of the marker
    iconAnchor: [15, 40],                 // Bottom center of the marker
    popupAnchor: [0, -35],                // Position of the popup
    className: 'walkabout-parking-marker' // CSS class for the marker
  });
};

/**
 * Creates a custom user location marker with pulsing effect using brand colors
 * @returns {L.Icon} Leaflet Icon instance
 */
const createUserLocationIcon = () => {
  // SVG for user location with pulsing animation using brand colors
  const userLocationSvg = `
    <svg width="44" height="44" viewBox="0 0 44 44" xmlns="http://www.w3.org/2000/svg">
      <circle cx="22" cy="22" r="20" fill="#276ACD" opacity="0.2">
        <animate attributeName="r" values="15;20;15" dur="2s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.2;0.3;0.2" dur="2s" repeatCount="indefinite" />
      </circle>
      <circle cx="22" cy="22" r="12" fill="#276ACD" opacity="0.4" />
      <circle cx="22" cy="22" r="8" fill="white" />
      <circle cx="22" cy="22" r="4" fill="#276ACD" />
    </svg>
  `;

  const svgUri = `data:image/svg+xml;base64,${btoa(userLocationSvg.trim())}`;

  return new L.Icon({
    iconUrl: svgUri,
    iconSize: [44, 44],
    iconAnchor: [22, 22],
    popupAnchor: [0, -22],
  });
};

/**
 * Creates a custom farmers market center marker icon with a clear, appealing design
 * @returns {L.Icon} Leaflet Icon instance
 */
const createFarmersMarketCenterIcon = () => {
  // Create SVG for farmers market center marker with attractive styling
  const primaryColor = '#2E8B57'; // Sea green - natural but distinctive
  const accentColor = '#FFFFFF'; // White for good contrast
  
  // Create a marker with a simple but clear farmers market basket symbol
  const markerSvg = `
    <svg width="36" height="46" viewBox="0 0 36 46" xmlns="http://www.w3.org/2000/svg">
      <!-- Pin shadow -->
      <ellipse cx="18" cy="44" rx="7" ry="2" fill="rgba(0,0,0,0.2)" />
      
      <!-- Pin shape - slightly larger for better visibility -->
      <path d="M18 0C8.059 0 0 8.059 0 18C0 27.941 18 46 18 46C18 46 36 27.941 36 18C36 8.059 27.941 0 18 0Z" fill="${primaryColor}"/>
      
      <!-- White circular background -->
      <circle cx="18" cy="18" r="14" fill="${accentColor}"/>
      
      <!-- Market basket symbol - simplified but recognizable -->
      <path d="M9 22L27 22L24 13L12 13Z" fill="${primaryColor}" stroke="#333333" stroke-width="0.5"/>
      <path d="M12 13C12 13 15 8 24 13" fill="none" stroke="#333333" stroke-width="1.5" stroke-linecap="round"/>
      
      <!-- Simple produce shapes - clear and clean -->
      <circle cx="14" cy="18" r="2.5" fill="#8BC34A"/> <!-- Apple/green produce -->
      <circle cx="18" cy="17" r="2.5" fill="#FF5722"/> <!-- Orange/red produce -->
      <circle cx="22" cy="18" r="2.5" fill="#FFEB3B"/> <!-- Yellow produce -->
    </svg>
  `;

  // Encode the SVG string to base64
  const svgUri = `data:image/svg+xml;base64,${btoa(markerSvg.trim())}`;
  
  return new L.Icon({
    iconUrl: svgUri,                      // SVG data URI
    iconSize: [34, 44],                   // Only slightly larger than standard pins
    iconAnchor: [17, 44],                 // Bottom center of the marker
    popupAnchor: [0, -40],                // Position of the popup
    className: 'walkabout-farmers-market-center-marker' // CSS class for the marker
  });
};

// Export all functions individually
export {
  createLocationMarkerIcon,
  createUserLocationIcon,
  createParkingMarkerIcon,
  createFarmersMarketCenterIcon
};
